import { castDraft, produce } from 'immer';

import { BearingEnvelope, RoofContainer } from '@paragon/contract-library';
import { Line2D, LineSegment2D, Polygon2D } from '@paragon/geometry-library';
import {
  calculateEdgesProjectedTo2D,
  createRoofBasePolygons,
  TrussSolidCalculationDependencies,
} from '@paragon/layout';
import { ReadonlyRecord } from '@paragon/utilities';

import { FundamentalLayoutData } from '../../../store/selectors/layoutDataSelectors';
import { FocusedFeaturePatternContainerData } from '../../../store/selectors/layoutGeometricEcosystemSelectors';
import {
  doesWorkLineBoundLevel,
  getBearingAreas,
  getBoundingWorkLineGuids,
  getFeaturePatternContainerData,
  getTrussEnvelopeData,
  getWorkLineGeometries,
  setFocusedObjectDerivedData,
} from './common';
import { GeometricEcosystem, SourcedSnap } from './GeometricEcosystem';
import { TrussEnvelopeData } from './TrussEnvelopeData';
import { getOverhangAreasForRoofContainer } from './util';

export class RoofContainerBasedGeometricEcosystem implements GeometricEcosystem {
  readonly source: FundamentalLayoutData;

  readonly roofAreas: ReadonlyRecord<string, Polygon2D[]>; // keyed by roof container guid
  readonly overhangAreas: ReadonlyRecord<string, Polygon2D[]>; // keyed by roof container guid
  readonly nonOverhangRoofSnaps: readonly SourcedSnap[];

  readonly bearingAreas: ReadonlyRecord<string, Polygon2D>; // keyed by bearing envelope guid
  readonly bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>;

  readonly workLines: ReadonlyRecord<string, Line2D | LineSegment2D>; // keyed by work line guid
  readonly boundingWorkLineGuids: ReadonlySet<string>;

  readonly snappingGuideLines: LineSegment2D[];

  readonly trussEnvelopes: ReadonlyRecord<string, TrussEnvelopeData>; // keyed by truss envelope guid

  readonly featurePatternContainers: ReadonlyRecord<string, Polygon2D>; // keyed by feature pattern container guid
  focusedFeaturePatternContainer!: Polygon2D | null;

  constructor(
    source: FundamentalLayoutData,
    allBearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
    focusedFeaturePatternContainerData: FocusedFeaturePatternContainerData,
    dependencies: TrussSolidCalculationDependencies,
    snappingGuideLines: LineSegment2D[],
  ) {
    this.source = source;

    const roofAreas: Record<string, Polygon2D[]> = {};
    const overhangAreas: Record<string, Polygon2D[]> = {};
    const roofSnaps: SourcedSnap[] = [];

    for (const roofContainer of Object.values(this.source.roofContainers)) {
      const roofBase = createRoofBasePolygons(roofContainer);
      roofAreas[roofContainer.guid] = roofBase;

      const roofContainerSegments = (
        roofContainer.solidData ? calculateEdgesProjectedTo2D(roofContainer.solidData) : []
      ).map((geometry) => ({ geometry }));
      roofSnaps.push(...roofContainerSegments);

      const overhangAreasForContainer = getOverhangAreasForRoofContainer(roofContainer);
      overhangAreas[roofContainer.guid] = overhangAreasForContainer;
    }

    this.roofAreas = roofAreas;
    this.nonOverhangRoofSnaps = roofSnaps;
    this.overhangAreas = overhangAreas;

    this.bearingAreas = getBearingAreas(this.source);
    this.bearingEnvelopes = this.source.bearingEnvelopes;
    this.workLines = getWorkLineGeometries(this.source);
    this.boundingWorkLineGuids = getBoundingWorkLineGuids(this.source);

    this.snappingGuideLines = snappingGuideLines;

    this.trussEnvelopes = getTrussEnvelopeData(this.source.trussEnvelopes, dependencies);

    this.featurePatternContainers = getFeaturePatternContainerData(
      this.source.featurePatternContainers,
      allBearingEnvelopes,
    );

    setFocusedObjectDerivedData(focusedFeaturePatternContainerData, allBearingEnvelopes, this);
  }

  getActiveRoofContainer(): RoofContainer {
    const roofContainerGuids = Object.keys(this.source.roofContainers);
    if (roofContainerGuids.length !== 1) {
      throw new Error('Geometric ecosystem contains multiple roof containers when only one was expected.');
    }

    return this.source.roofContainers[roofContainerGuids[0]];
  }

  getRoofAreas() {
    return Object.values(this.roofAreas).flat();
  }

  getOverhangAreas() {
    return Object.values(this.overhangAreas).flat();
  }

  getRoofSnapsWithSources() {
    return this.nonOverhangRoofSnaps.concat(
      Object.entries(this.overhangAreas).flatMap(([roofContainerGuid, polygons]) =>
        polygons.flatMap((polygon) => polygon.edges.map((geometry) => ({ geometry, roofContainerGuid }))),
      ),
    );
  }

  getCeilingSnapsWithSources(): readonly SourcedSnap[] {
    return []; // We treat the entirety of a roof container as "Roof".
  }

  getTrussEnvelopes() {
    return Object.values(this.trussEnvelopes);
  }

  getTrussEnvelopeDictionary() {
    return this.trussEnvelopes;
  }

  getBearingAreas() {
    return this.bearingAreas;
  }

  getBearingEnvelopes() {
    return this.bearingEnvelopes;
  }

  getWorkLineDictionary() {
    return this.workLines;
  }

  getBoundingWorkLines(levelGuid: string | null) {
    return Object.entries(this.workLines)
      .filter(
        ([guid]) =>
          this.boundingWorkLineGuids.has(guid) && doesWorkLineBoundLevel(this.source.workLines[guid], levelGuid),
      )
      .map(([, line]) => line);
  }

  getUploadedSnappingLines(): LineSegment2D[] {
    return this.snappingGuideLines;
  }

  getFeaturePatternContainerDictionaryIn2D() {
    return this.featurePatternContainers;
  }

  getFeaturePatternContainerPolygonsIn2D() {
    return Object.values(this.featurePatternContainers);
  }

  getFocusedFeaturePatternContainerPolygonIn2D() {
    return this.focusedFeaturePatternContainer;
  }

  static createForSingleRoofContainer(
    roofContainerGuid: string | null,
    data: FundamentalLayoutData,
    allBearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
    focusedFeaturePatternContainerData: FocusedFeaturePatternContainerData,
    dependencies: TrussSolidCalculationDependencies,
    snappingGuideLines: LineSegment2D[],
    options?: { excludedTrussEnvelopes?: string[] },
  ): RoofContainerBasedGeometricEcosystem | null {
    if (roofContainerGuid == null || !(roofContainerGuid in data.roofContainers)) {
      return null;
    }

    const roofContainer = data.roofContainers[roofContainerGuid];

    const censoredData = produce(data, (draft) => {
      // If we have been requested to exclude any truss envelopes, do so first.
      if (options?.excludedTrussEnvelopes) {
        for (const guid of options.excludedTrussEnvelopes) {
          delete draft.trussEnvelopes[guid];
        }
      }

      // Next, ensure that only truss envelopes relevant to the roof container are present.
      for (const trussEnvelope of Object.values(draft.trussEnvelopes)) {
        if (trussEnvelope.roofContainerGuid !== roofContainer.guid) {
          delete draft.trussEnvelopes[trussEnvelope.guid];
        }
      }

      draft.roofContainers = { [roofContainer.guid]: castDraft(roofContainer) };
    });

    return new RoofContainerBasedGeometricEcosystem(
      censoredData,
      allBearingEnvelopes,
      focusedFeaturePatternContainerData,
      dependencies,
      snappingGuideLines,
    );
  }
}
